import React, { useState, useEffect } from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbMapPin } from 'react-icons/tb';
import { Auth } from 'aws-amplify';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ConfirmationModal from '../../../templates/ConfirmationModal';
import useCities from '../../../../hooks/useCities';
import useProvinces from '../../../../hooks/useProvinces';
import streetTypesData from '../../../../resources/street_types.json';
import appConfig from '../../../../config/app-config';

// ✅ URL GENÉRICA - Funciona para PF e PJ
const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;
const BASE_URL = `${PERSON_API_URL}/person`;

const { address: GEOLOCATION_API_URL, key: GEOLOCATION_API_KEY } = appConfig.apis.geolocationApiEnv;

interface Address {
    id?: string;
    zipcode: string;
    streetType: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    cityId: string;
    ibgeCode?: string;
}

interface Props {
    personId: string;  // ✅ UUID genérico (Individual ou Legal Person)
    personType?: 'individual' | 'legal_person';  // ✅ Para IDs únicos
    addresses?: Address[];
    onAddressesChange?: (addresses: Address[]) => void;
}

const PersonFormAddresses: React.FC<Props> = ({
    personId,
    personType = 'individual',  // ✅ Default para compatibilidade
    addresses: initialAddresses = [],
    onAddressesChange = () => { }
}) => {
    const { fetchCities } = useCities();
    const { fetchProvinces } = useProvinces();

    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);
    const provinces = useSignal<any[]>([]);
    const cities = useSignal<any[]>([]);
    const selectedProvince = useSignal<string>('');
    const addresses = useSignal<Address[]>([]);

    const [editingAddress, setEditingAddress] = useState<Address | null>(null);
    const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
    const [isZipcodeFocused, setIsZipcodeFocused] = useState<boolean>(false);
    const [form, setForm] = useState<Address>({
        zipcode: '',
        streetType: 'Rua',
        street: '',
        number: '',
        complement: '',
        district: '',
        cityId: '',
        ibgeCode: ''
    });

    // ✅ MANTER FUNÇÃO DE AUTOFILL IDÊNTICA
    const handleZipcodeChange = async (value: string) => {
        value = value.replace(/[^0-9]/g, "");
        setForm(prev => ({ ...prev, zipcode: value }));

        if (value.length === 0) {
            setForm(prev => ({
                ...prev,
                streetType: 'Rua',
                street: '',
                district: '',
                complement: '',
                cityId: ''
            }));
            selectedProvince.value = '';
            cities.value = [];
            return;
        }

        if (value.length === 8) {
            try {
                const response = await fetch(`https://viacep.com.br/ws/${value}/json/`);
                const data = await response.json();

                if (data.erro) {
                    console.warn("CEP não encontrado:", value);
                    return;
                }

                const logradouro = data.logradouro || "";
                const streetTypeName = logradouro.split(" ")[0] || "";

                const matchingStreetType = streetTypesData.streetTypes.find(st =>
                    st.name === streetTypeName || st.abbreviations.includes(streetTypeName)
                );

                const streetWithoutType = matchingStreetType
                    ? logradouro.replace(streetTypeName, "").trim()
                    : logradouro;

                setForm(prev => ({
                    ...prev,
                    streetType: matchingStreetType?.name || streetTypeName || 'Rua',
                    street: streetWithoutType,
                    district: data.bairro || '',
                    complement: data.complemento || '',
                    ibgeCode: data.ibge || ''
                }));

                if (data.uf) {
                    setForm(prev => ({ ...prev, cityId: '' }));
                    selectedProvince.value = data.uf;
                    await loadCities(data.uf);

                    if (data.ibge) {
                        setTimeout(() => {
                            const cityMatch = cities.value.find((city: any) => city.ibgeCode === data.ibge);
                            if (cityMatch) {
                                setForm(prev => ({ ...prev, cityId: cityMatch.value }));
                                console.log("✅ Cidade selecionada automaticamente:", cityMatch.label);
                            } else {
                                console.log("⚠️ Cidade não encontrada pelo IBGE:", data.ibge);
                            }
                        }, 500);
                    }
                }

                console.log("✅ Autofill por CEP realizado:", data);
            } catch (error) {
                console.error('❌ Erro ao buscar CEP:', error);
            }
        }
    };

    const streetTypes = streetTypesData.streetTypes.map(st => ({
        value: st.name,
        label: st.name
    }));

    useEffect(() => {
        if (initialAddresses && initialAddresses.length > 0) {
            addresses.value = initialAddresses;
        }

        loadProvinces();

        if (personId) {
            console.log('🔄 Carregando endereços para person:', personId);
            loadAddresses();
        }
    }, [personId]);

    const loadProvinces = async () => {
        try {
            const provincesData = await fetchProvinces();
            provinces.value = provincesData.map((province: any) => ({
                value: province.initials,
                label: `${province.name} (${province.initials})`
            }));
        } catch (error) {
            console.error('Error loading provinces:', error);
        }
    };

    const loadCities = async (provinceInitials: string) => {
        try {
            const citiesData = await fetchCities(provinceInitials);
            cities.value = citiesData.map((city: any) => ({
                value: city.id,
                label: city.name,
                ibgeCode: city.ibgeCode
            }));
        } catch (error) {
            console.error('Error loading cities:', error);
        }
    };

    const handleProvinceChange = (provinceInitials: string) => {
        selectedProvince.value = provinceInitials;
        setForm(prev => ({ ...prev, cityId: '' }));
        if (provinceInitials) {
            loadCities(provinceInitials);
        }
    };

    const openModal = async (address?: Address) => {
        if (address) {
            setEditingAddress(address);
            setForm(address);

            if (address.cityId) {
                console.log("🔍 Carregando dados da cidade:", address.cityId);
                try {
                    const url = `${GEOLOCATION_API_URL}/cities/${address.cityId}`;

                    const response = await fetch(url, {
                        headers: {
                            'x-api-key': GEOLOCATION_API_KEY
                        }
                    });

                    if (response.ok) {
                        const city = await response.json();

                        if (city && city.province) {
                            const provinceInitials = city.province.initials;
                            selectedProvince.value = provinceInitials;
                            await loadCities(provinceInitials);
                        }
                    }
                } catch (error) {
                    console.error("❌ Erro ao buscar dados da cidade:", error);
                }
            }
        } else {
            setEditingAddress(null);
            setForm({
                zipcode: '',
                streetType: 'Rua',
                street: '',
                number: '',
                complement: '',
                district: '',
                cityId: '',
                ibgeCode: ''
            });
            selectedProvince.value = '';
            cities.value = [];
        }
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingAddress(null);
        setForm({
            zipcode: '',
            streetType: 'Rua',
            street: '',
            number: '',
            complement: '',
            district: '',
            cityId: '',
            ibgeCode: ''
        });
        selectedProvince.value = '';
        cities.value = [];
    };

    const handleSubmit = async () => {
        if (!form.zipcode || !form.street || !form.number || !form.district || !form.cityId) {
            toast.error('Preencha todos os campos obrigatórios');
            return;
        }

        isLoading.value = true;
        try {
            const url = editingAddress
                ? `${BASE_URL}/${personId}/addresses/${editingAddress.id}`
                : `${BASE_URL}/${personId}/addresses`;

            const method = editingAddress ? 'PUT' : 'POST';

            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const payload = {
                ...form,
                street_type: form.streetType,
                city_id: form.cityId
            };
            const { streetType, cityId, id, ibgeCode, ...cleanPayload } = payload;

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': PERSON_API_KEY,
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(cleanPayload)
            });

            if (response.ok) {
                toast.success(editingAddress ? 'Endereço atualizado!' : 'Endereço cadastrado!');
                await loadAddresses();
                closeModal();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao salvar endereço');
            }
        } catch (error) {
            toast.error('Erro ao salvar endereço');
            console.error('Error saving address:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!addressToDelete) return;

        isLoading.value = true;
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${BASE_URL}/${personId}/addresses/${addressToDelete}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': PERSON_API_KEY,
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success('Endereço excluído!');
                await loadAddresses();
            } else {
                toast.error('Erro ao excluir endereço');
            }
        } catch (error) {
            toast.error('Erro ao excluir endereço');
            console.error('Error deleting address:', error);
        } finally {
            isLoading.value = false;
            isDeleteModalOpen.value = false;
            setAddressToDelete(null);
        }
    };

    const loadAddresses = async () => {
        if (!personId) {
            console.log('⚠️ personId está vazio, não carregando endereços');
            return;
        }

        console.log('🔍 Carregando endereços para personId:', personId);
        isLoading.value = true;

        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${BASE_URL}/${personId}/addresses`, {
                headers: {
                    'x-api-key': PERSON_API_KEY,
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                addresses.value = data.addresses || data || [];
                console.log('✅ Endereços carregados:', addresses.value.length);
            } else {
                const errorText = await response.text();
                console.error('❌ Erro na resposta:', response.status, response.statusText, errorText);
            }
        } catch (error) {
            console.error('❌ Error loading addresses:', error);
            addresses.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    const confirmDelete = (addressId: string) => {
        setAddressToDelete(addressId);
        isDeleteModalOpen.value = true;
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TbMapPin className="text-blue-600" />
                    Endereços
                </h3>
                <button
                    id={`btn-add-address-${personType}`}  // ✅ ID único por tipo
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
                >
                    <TbPlus size={16} />
                    Novo Endereço
                </button>
            </div>

            <div className="bg-white rounded-lg shadow">
                {isLoading.value ? (
                    <div className="p-8 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-sm">Carregando endereços...</p>
                    </div>
                ) : addresses.value.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <TbMapPin size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>Nenhum endereço cadastrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Endereço</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">CEP</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Bairro</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {addresses.value.map((address, index) => (
                                    <tr key={address.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div>
                                                <div className="font-medium">
                                                    {address.streetType} {address.street}, {address.number}
                                                </div>
                                                {address.complement && (
                                                    <div className="text-sm text-gray-500">{address.complement}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">{address.zipcode}</td>
                                        <td className="px-4 py-3 text-sm">{address.district}</td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    id={`btn-edit-address-${personType}-${index}`}  // ✅ ID único
                                                    onClick={() => openModal(address)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Editar"
                                                >
                                                    <TbEdit size={16} />
                                                </button>
                                                <button
                                                    id={`btn-remove-address-${personType}-${index}`}  // ✅ ID único
                                                    onClick={() => confirmDelete(address.id!)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Excluir"
                                                >
                                                    <TbTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Cadastro/Edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingAddress ? 'Editar Endereço' : 'Novo Endereço'}
                        </h3>

                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-2">
                                <TextEdit
                                    id={`zipcode-${personType}`}
                                    label="CEP *"
                                    value={form.zipcode}
                                    onChange={(e) => handleZipcodeChange(e.target.value)}
                                    onFocus={() => setIsZipcodeFocused(true)}
                                    onBlur={() => setIsZipcodeFocused(false)}
                                    placeholder={isZipcodeFocused || form.zipcode ? "00000-000" : ""}
                                />
                            </div>

                            <div className="col-span-2">
                                <SelectTextField
                                    id={`streetType-${personType}`}
                                    label="Tipo *"
                                    value={form.streetType}
                                    onChange={(e) => setForm(prev => ({ ...prev, streetType: e.target.value }))}
                                    items={streetTypes}
                                />
                            </div>

                            <div className="col-span-5">
                                <TextEdit
                                    id={`street-${personType}`}
                                    label="Logradouro *"
                                    value={form.street}
                                    onChange={(e) => setForm(prev => ({ ...prev, street: e.target.value }))}
                                />
                            </div>

                            <div className="col-span-1">
                                <TextEdit
                                    id={`number-${personType}`}
                                    label="Número *"
                                    value={form.number}
                                    onChange={(e) => setForm(prev => ({ ...prev, number: e.target.value }))}
                                />
                            </div>

                            <div className="col-span-2">
                                <TextEdit
                                    id={`complement-${personType}`}
                                    label="Complemento"
                                    value={form.complement}
                                    onChange={(e) => setForm(prev => ({ ...prev, complement: e.target.value }))}
                                />
                            </div>

                            <div className="col-span-4">
                                <TextEdit
                                    id={`district-${personType}`}
                                    label="Bairro *"
                                    value={form.district}
                                    onChange={(e) => setForm(prev => ({ ...prev, district: e.target.value }))}
                                />
                            </div>

                            <div className="col-span-2">
                                <SelectTextField
                                    id={`province-${personType}`}
                                    label="Estado *"
                                    value={selectedProvince.value}
                                    onChange={(e) => handleProvinceChange(e.target.value)}
                                    items={provinces.value}
                                    placeholder="Selecione o estado"
                                />
                            </div>

                            <div className="col-span-4">
                                <SelectTextField
                                    id={`cityId-${personType}`}
                                    label="Cidade *"
                                    value={form.cityId}
                                    onChange={(e) => setForm(prev => ({ ...prev, cityId: e.target.value }))}
                                    items={cities.value}
                                    placeholder="Selecione a cidade"
                                    disabled={!selectedProvince.value}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                id={`btn-cancel-address-${personType}`}  // ✅ ID único
                                onClick={closeModal}
                                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                id={`btn-save-address-${personType}`}  // ✅ ID único
                                onClick={handleSubmit}
                                disabled={isLoading.value}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading.value ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => {
                    isDeleteModalOpen.value = false;
                    setAddressToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Excluir Endereço"
                id={addressToDelete || ''}
                text="Tem certeza que deseja excluir este endereço? Esta ação não pode ser desfeita."
            />
        </div>
    );
};

export default PersonFormAddresses;
